/**
 * One curriculum group (業種/コース) of the learner's カリキュラムの進捗 block.
 *
 * Collapsed: white row with name + stacked WEEK bar + 完了/総WEEK counter + chevron down.
 * Expanded: the same single row, now on the tinted block with the chevron flipped up,
 * followed by the WEEK chips with legend.
 * The admin side stops there — per-WEEK level detail stays in the learner app.
 */

import { TCurriculumGroupDto } from "@/api/services/main/learning-progress/dtos/get-learner-curriculum-progress.dto";
import { IconChevronDown } from "@/assets/icons";
import { CurriculumProgressBar } from "@/features/learning-progress/components/learner-detail/curriculum-progress/CurriculumProgressBar";
import { WeekStatusChips } from "@/features/learning-progress/components/learner-detail/curriculum-progress/WeekStatusChips";
import { TCurriculumGroupCounts, countGroupStatuses } from "@/features/learning-progress/utils/curriculum-groups";
import { background, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded, spacing } from "@/theme/variables";
import { Collapse, Stack, Typography } from "@mui/material";
import { FC, memo, useCallback, useMemo, useState } from "react";

type TCurriculumGroupAccordionProps = {
  group: TCurriculumGroupDto;
};

/** Width reserved for the group name so every bar starts at the same x — kept tight so the bar runs long */
const NAME_WIDTH = 88;

/**
 * The single toggle row: group name + stacked bar + 完了/総WEEK counter + chevron.
 * Rendered once in both states — the chevron points down when collapsed and up when expanded.
 */
const GroupSummary: FC<{ name: string; counts: TCurriculumGroupCounts; expanded: boolean; onToggle: () => void }> = ({
  name,
  counts,
  expanded,
  onToggle,
}) => (
  <Stack direction="row" alignItems="center" gap={spacing.xs} sx={{ cursor: "pointer" }} onClick={onToggle}>
    {/* 12px semi — no cap12Semi preset exists, so the weight is set on top of cap12 */}
    <Typography sx={{ ...typography.cap12, fontWeight: 600, color: text.primary, width: NAME_WIDTH, flexShrink: 0 }}>{name}</Typography>

    <CurriculumProgressBar counts={counts} />

    <Typography sx={{ ...typography.cap12, fontWeight: 600, color: text.secondary, whiteSpace: "nowrap" }}>
      {counts.completed}/{counts.total}
    </Typography>

    <Stack sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
      <IconChevronDown width={24} height={24} />
    </Stack>
  </Stack>
);

const CurriculumGroupAccordionComponent: FC<TCurriculumGroupAccordionProps> = ({ group }) => {
  const counts = useMemo(() => countGroupStatuses(group.subjects), [group.subjects]);
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    /* Collapsed the whole group stays on the block's white; expanded the entire block is tinted */
    <Stack
      sx={{
        borderRadius: rounded.sm,
        bgcolor: expanded ? background.secondary : background.white,
        px: spacing.xs,
        py: expanded ? spacing.xs : 0,
        gap: spacing.xxs,
        transition: "background-color 0.2s",
      }}
    >
      <GroupSummary name={group.name} counts={counts} expanded={expanded} onToggle={handleToggleExpand} />

      <Collapse in={expanded} timeout={200} unmountOnExit>
        <WeekStatusChips subjects={group.subjects} counts={counts} />
      </Collapse>
    </Stack>
  );
};

export const CurriculumGroupAccordion = memo(CurriculumGroupAccordionComponent);
